import { forwardRef } from 'react'

import { Link as RouterLink } from 'react-router-dom'
import styled from 'styled-components'

// Props interface that combines both RouterLink and anchor props
interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
	children: React.ReactNode
	to?: string
	external?: boolean
	target?: string
	cssVars?: Record<string, string>
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
	({ children, to, external = false, target, cssVars = {}, ...props }, ref) => {
		// Create dynamic CSS variables
		const dynamicStyles = {
			'--link-color': cssVars.color || 'var(--foreground)',
			'--link-text-decoration': cssVars.textDecoration || 'underline',
			'--link-color-hover': cssVars.hoverColor || 'var(--foreground-hover)',
			'--link-text-decoration-hover':
				cssVars.hoverTextDecoration || 'underline',
		} as React.CSSProperties

		// If it's an external link or has a target="_blank", use a regular anchor tag
		if (external || target === '_blank' || !to) {
			return (
				<LinkStyled
					{...props}
					href={to}
					target={target}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
					ref={ref}
					style={dynamicStyles}
				>
					{children}
				</LinkStyled>
			)
		}

		// Otherwise use React Router's Link for internal navigation
		return (
			<RouterLinkStyled to={to} {...props} style={dynamicStyles}>
				{children}
			</RouterLinkStyled>
		)
	}
)

// For typescript naming in dev tools
Link.displayName = 'Link'

// Styled components with proper theme typing
export const LinkStyled = styled.a`
	color: var(--link-color);
	text-decoration: var(--link-text-decoration);

	&:hover {
		text-decoration: var(--link-text-decoration-hover);
		color: var(--link-color-hover);
	}
`

// Styled RouterLink for internal navigation
const RouterLinkStyled = styled(RouterLink)`
	color: var(--link-color);
	text-decoration: var(--link-text-decoration);

	&:hover {
		text-decoration: var(--link-text-decoration-hover);
		color: var(--link-color-hover);
	}
`
